import { PageHeaderWrapper, Pill, Title, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Box, Stack, ThemeProvider } from "@mui/material";
import { isCapacitorPlatform } from "@src/appV2/lib";
import { PullToRefresh } from "@src/appV2/lib/PullToRefresh/PullToRefresh";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { useIsParentScrolled } from "@src/appV2/lib/utils/useIsParentScrolled";
import { useIsDeviceLocationPermissionGranted } from "@src/appV2/Location/deviceLocation/geoLocation";
import { ModalRoute } from "@src/appV2/redesign/Navigation/ModalRoute";
import { ModalTransitionSwitch } from "@src/appV2/redesign/Navigation/ModalTransitionSwitch";
import { useEffect, useMemo, useRef } from "react";
import { useRouteMatch } from "react-router-dom";

import { Button } from "../components/Button";
import { PageWrapper } from "../components/PageWrapper";
import { FailedPayoutBannerContainer } from "../Payments/FailedPayouts/components/FailedPayoutBannerContainer";
import { LocationPermissionsBottomSheet } from "../Shift/Bookability/LocationPermissionsBottomSheet";
import { BookingsContainer } from "./components/BookingsContainer";
import { WorkerShiftsCalloutsContainer } from "./components/CalloutsContainer";
import { PageContent } from "./components/PageContent";
import { useWorkerBookingsData } from "./hooks/useWorkerBookingsData";
import {
  MY_SHIFTS_BACKGROUND_CHECK_ERROR_PATH,
  MY_SHIFTS_FULL_PATH,
  SHIFT_HISTORY_FULL_PATH,
} from "./path";
import { BackgroundCheckErrorBottomSheet } from "./ShiftDetails/components/BackgroundCheckErrorBottomSheet";
import { useIsSeePastShiftsEnabled } from "./UnverifiedShifts/useIsSeePastShiftsEnabled";

export function WorkerShiftsPage() {
  const match = useRouteMatch();

  const {
    data: isDeviceLocationPermissionGranted,
    isSuccess: isDeviceLocationPermissionGrantedSuccess,
    isLoading: isDeviceLocationPermissionGrantedLoading,
  } = useIsDeviceLocationPermissionGranted();

  const {
    // Booking data
    scheduledBookings,
    highlightedBooking,
    highlightedShiftPayBreakdown,
    hasUpcomingShifts,

    // Loading states
    isLoading: isLoadingBookings,
    isLoadingShiftInvites,
    isLoadingVisitInvites,
    isLoadingUnverifiedShifts,
    isLoadingNegotiations,
    isLoadingSentHome,
    isLoadingExtraWorked,
    isLoadingBookingRequests,

    // Callout data
    shiftInvites,
    visitInviteCount,
    unverifiedShifts,
    openNegotiationsCount,
    sentHomeRequestsData,
    extraWorkedTimeRequestsData,
    bookingRequestsData,

    // UI state
    showEmptyShiftState,

    // Actions
    refreshAllData,
  } = useWorkerBookingsData();

  const isSeePastShiftsEnabled = useIsSeePastShiftsEnabled();

  const currentDate = useCurrentDate(1000);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrolled = useIsParentScrolled(scrollContainerRef);

  const unverifiedShiftsCount = useMemo(
    () =>
      unverifiedShifts.filter((shift) => !shift.hasPendingFullShiftExtraWorkedTimeRequest).length,
    [unverifiedShifts]
  );

  const locationPermissionsModalState = useModalState();
  const geolocationIsUnavailable =
    isDeviceLocationPermissionGrantedSuccess &&
    isDeviceLocationPermissionGranted === false &&
    isCapacitorPlatform();

  useEffect(() => {
    if (geolocationIsUnavailable && hasUpcomingShifts) {
      locationPermissionsModalState.openModal();
    }
  }, [geolocationIsUnavailable, locationPermissionsModalState, hasUpcomingShifts]);

  const isLoading = isLoadingBookings || isDeviceLocationPermissionGrantedLoading;

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <PageHeaderWrapper
          variant="tertiary"
          sx={{
            paddingBottom: 3,
            borderBottom: (theme) =>
              `1px solid ${isScrolled ? theme.palette.border?.primary : "transparent"}`,
          }}
        >
          <Stack spacing={4} direction="column" sx={{ paddingTop: 5 }}>
            <Box sx={{ paddingX: 4 }}>
              <WorkerShiftsCalloutsContainer
                totalInviteCount={(shiftInvites?.data.length ?? 0) + visitInviteCount}
                bookingRequestsCount={bookingRequestsData?.data.length ?? 0}
                unverifiedShiftsCount={unverifiedShifts.length}
                activeNegotiationsCount={openNegotiationsCount?.totalNegotiatingShiftsCount ?? 0}
                extraWorkedRequestsCount={(extraWorkedTimeRequestsData?.data ?? []).length}
                sentHomeRequestsCount={(sentHomeRequestsData ?? []).length}
                isLoadingInvites={isLoadingShiftInvites || isLoadingVisitInvites}
                isLoadingUnverifiedShifts={isLoadingUnverifiedShifts}
                isLoadingNegotiations={isLoadingNegotiations}
                isLoadingSentHome={isLoadingSentHome}
                isLoadingExtraWorked={isLoadingExtraWorked}
                isLoadingBookingRequests={isLoadingBookingRequests}
                isSeePastShiftsEnabled={isSeePastShiftsEnabled}
              />
            </Box>
            <FailedPayoutBannerContainer
              sourceUI="bookings-tab"
              sx={(theme) => ({ paddingX: theme.spacing(5), paddingY: theme.spacing(2) })}
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ paddingX: 5 }}
            >
              <Title semibold component="h4" variant="h4">
                Bookings
              </Title>
              {isSeePastShiftsEnabled && (
                <Button
                  startIconType="clock-history"
                  variant="outlined"
                  size="small"
                  href={SHIFT_HISTORY_FULL_PATH}
                >
                  Shift history{" "}
                  {unverifiedShiftsCount > 0 && (
                    <Pill
                      label={String(unverifiedShiftsCount)}
                      size="small"
                      sx={(theme) => ({
                        marginLeft: 3,
                        color: theme.palette.error.main,
                        backgroundColor: theme.palette.intent?.error.background,
                      })}
                    />
                  )}
                </Button>
              )}
            </Stack>
          </Stack>
        </PageHeaderWrapper>

        <PullToRefresh refreshHeight={96} onRefresh={refreshAllData} />

        <PageContent ref={scrollContainerRef}>
          <BookingsContainer
            isLoading={isLoading}
            bookings={scheduledBookings}
            highlightedBooking={highlightedBooking}
            shiftPayBreakdown={highlightedShiftPayBreakdown}
            currentDate={currentDate}
            showEmptyShiftState={showEmptyShiftState}
          />
        </PageContent>

        <LocationPermissionsBottomSheet
          modalState={locationPermissionsModalState}
          isNotNowButtonVisible={false}
        />

        <ModalTransitionSwitch>
          <ModalRoute
            shouldReplacePathOnBack
            closeModalPath={MY_SHIFTS_FULL_PATH}
            path={`${match.url}${MY_SHIFTS_BACKGROUND_CHECK_ERROR_PATH}`}
            render={({ modalState }) => <BackgroundCheckErrorBottomSheet modalState={modalState} />}
          />
        </ModalTransitionSwitch>
      </PageWrapper>
    </ThemeProvider>
  );
}
